<script setup lang="ts">
import { computed } from 'vue'

type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'ghost' | 'outline' | 'ghost-primary' | 'ghost-danger'
type ButtonSize = 'sm' | 'md' | 'lg'

const props = withDefaults(defineProps<{
  variant?: ButtonVariant
  size?: ButtonSize
  disabled?: boolean
  loading?: boolean
  block?: boolean
  icon?: boolean
  type?: 'button' | 'submit' | 'reset'
}>(), {
  variant: 'primary',
  size: 'md',
  disabled: false,
  loading: false,
  block: false,
  icon: false,
  type: 'button'
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const buttonClass = computed(() => [
  'base-btn',
  `base-btn--${props.variant}`,
  `base-btn--${props.size}`,
  {
    'base-btn--loading': props.loading,
    'base-btn--block': props.block,
    'base-btn--icon': props.icon,
    // 纯图标钮点击热区外扩（视觉不变），避免必须精准点到小图标
    'hit-expand': props.icon
  }
])

const isDisabled = computed(() => props.disabled || props.loading)
</script>

<template>
  <button
    :class="buttonClass"
    :type="type"
    :disabled="isDisabled"
    @click="emit('click', $event)"
  >
    <span v-if="loading" class="base-btn__spinner" />
    <slot v-else />
  </button>
</template>

<style lang="scss" scoped>
.base-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-2);
  border-radius: var(--btn-radius);
  font-weight: var(--font-weight-medium);
  font-size: var(--text-sm);
  line-height: 1;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all var(--transition-fast);
  text-decoration: none;
  white-space: nowrap;
  letter-spacing: 0;
  position: relative;
  user-select: none;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px var(--white), 0 0 0 4px var(--primary-200);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  // ── Sizes ──

  &--sm {
    height: 28px;
    padding: 0 var(--spacing-4);
    font-size: var(--text-xs);
    gap: var(--spacing-1);

    &.base-btn--icon { width: 28px; padding: 0; }
  }

  &--md {
    height: 32px;
    padding: 0 var(--spacing-5);

    &.base-btn--icon { width: 32px; padding: 0; }
  }

  &--lg {
    height: 36px;
    padding: 0 var(--spacing-6);
    font-size: var(--text-base);

    &.base-btn--icon { width: 36px; padding: 0; }
  }

  // ── Variants ──

  &--primary {
    background: var(--primary);
    color: var(--white);

    &:hover:not(:disabled) {
      background: var(--primary-dark);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
    &:active:not(:disabled) { background: var(--primary-dark); }
  }

  &--secondary {
    background: var(--primary-50);
    color: var(--primary-dark);
    border-color: var(--primary-100);

    &:hover:not(:disabled) {
      background: var(--primary-100);
      border-color: var(--primary-200);
    }
    &:active:not(:disabled) { background: var(--primary-200); }
  }

  &--success {
    background: var(--success-50);
    color: var(--success-text);
    border-color: var(--success-light);

    &:hover:not(:disabled) { background: var(--success-light); }
    &:active:not(:disabled) { background: var(--success-200); }
  }

  &--danger {
    background: var(--danger-50);
    color: var(--danger-text);
    border-color: var(--danger-light);

    &:hover:not(:disabled) { background: var(--danger-light); }
    &:active:not(:disabled) { background: var(--danger-200); }
  }

  &--warning {
    background: var(--warning-50);
    color: var(--warning-text);
    border-color: var(--warning-light);

    &:hover:not(:disabled) { background: var(--warning-light); }
    &:active:not(:disabled) { background: var(--warning-200); }
  }

  &--ghost {
    background: transparent;
    color: var(--text-secondary);

    &:hover:not(:disabled) {
      background: var(--surface-dim);
      color: var(--text-primary);
    }
    &:active:not(:disabled) { background: var(--surface-dim); }
  }

  &--ghost-primary {
    background: transparent;
    color: var(--primary);

    &:hover:not(:disabled) {
      background: var(--primary-50);
      color: var(--primary);
    }
    &:active:not(:disabled) { background: var(--primary-100); }
  }

  &--ghost-danger {
    background: transparent;
    color: var(--danger);

    &:hover:not(:disabled) {
      background: var(--danger-50);
      color: var(--danger);
    }
    &:active:not(:disabled) { background: var(--danger-100); }
  }

  &--outline {
    background: var(--white);
    color: var(--text-primary);
    border-color: var(--border-color);

    &:hover:not(:disabled) {
      background: var(--surface-dim);
      border-color: var(--border-hover);
    }
    &:active:not(:disabled) { background: var(--surface-dim); }
  }

  // ── States ──

  &--block { width: 100%; }

  &--loading {
    color: transparent !important;
    pointer-events: none;
  }

  &__spinner {
    position: absolute;
    width: 14px;
    height: 14px;
    border: 1.5px solid currentColor;
    border-right-color: transparent;
    border-radius: var(--radius-full);
    animation: btn-spin 0.6s linear infinite;
  }

  &--primary.base-btn--loading .base-btn__spinner {
    border-color: var(--glass-highlight);
    border-top-color: var(--white);
    border-right-color: transparent;
  }

  &--secondary.base-btn--loading .base-btn__spinner,
  &--ghost.base-btn--loading .base-btn__spinner,
  &--outline.base-btn--loading .base-btn__spinner {
    border-color: var(--border-color);
    border-top-color: var(--primary);
    border-right-color: transparent;
  }

  &--danger.base-btn--loading .base-btn__spinner,
  &--ghost-danger.base-btn--loading .base-btn__spinner {
    border-color: var(--danger-alpha-20);
    border-top-color: var(--danger-text);
    border-right-color: transparent;
  }

  &--success.base-btn--loading .base-btn__spinner {
    border-color: var(--success-alpha-20);
    border-top-color: var(--success-text);
    border-right-color: transparent;
  }

  &--warning.base-btn--loading .base-btn__spinner {
    border-color: var(--warning-alpha-20);
    border-top-color: var(--warning-text);
    border-right-color: transparent;
  }
}

@keyframes btn-spin {
  to { transform: rotate(360deg); }
}
</style>
